"use client";
import EmblaExclusiveSlider from "@/templates/slider/emblaExclusiveSlider";
import {
  useAppDispatch,
  useAppSelector,
  useCallback,
  useEffect,
  useState,
} from "@/utils/hooks/reactUseCustomHooks";
import { Box } from "@dxp-feed/ui/Box";
import UserLoggedInFallback from "../FallbackLayout/UserLoggedInFallback";
import { useLogin } from "@/helpers/useCommonFunctions";
import { fetchFavoirateOffers } from "@/utils/redux/favOffers/actions";
import { sceneStatesTrigger } from "@/utils/redux/gigya/scene";
import { dataLayerProductViewList } from "@/helpers/dataLayer";
import Link from "@/components/Link";
import { Text, Toast } from "@dxp-feed/ui";
import { useGigya } from "@dxp-feed/gigya";
import { useGigyaAccountInfo } from "@/helpers/gigyaCentralSsoHook";
import OffersCarousalSkeleton from "@/components/OffersCarousalSkeleton";
import { removeEmptyElements } from "@/utils/cleanHTML";

export default function OffersCarousal({ headingParam }: { headingParam?: any }) {
  const { loadLogin } = useLogin();
  const {
    loggedIn: userLoggedIn,
    isLoading,
    scene,
    uid,
  } = useAppSelector((state) => state.gigyaslice);
  const { activeOffers, loading: isOfferLoaded } = useAppSelector(
    (state) => state.offersSlice
  );
  const [loading, setLoading] = useState(true);
  const [addedToListOffers, setAddedToListOffers] = useState<any>([]);
  const dispatch = useAppDispatch();
  const { favOffers, offersToBeAdded } = useAppSelector(
    (state) => state.favoirateOffersSlice
  );
  const [offers, setOffers] = useState<any>([]);

  const [isOpen, setIsOpen] = useState(false);
  const openDialog = () => setIsOpen(true);
  const closeDialog = () => setIsOpen(false);
  const [visibleItems, setVisibleItems] = useState<Set<string>>(new Set());
  const [sentItems, setSentItems] = useState<Set<string>>(new Set());
  const [isAllSent, setIsAllSent] = useState(false);


  const handleVisibilityChange = useCallback(
    (item: any, isVisible: boolean) => {
      const key = item.item_id;

      if (isAllSent) return;

      setVisibleItems((prev) => {
        const updated = new Set(prev);
        isVisible ? updated.add(key) : updated.delete(key);
        return updated;
      });
    },
    [isAllSent]
  );
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isOpen) {
      timer = setTimeout(() => {
        setIsOpen(false);
      }, 3000);
    }
    return () => clearTimeout(timer);
  }, [isOpen]);

  useEffect(() => {
    if (isAllSent || visibleItems.size === 0) return;

    const newItems = [...visibleItems].filter((id) => !sentItems.has(id));
    if (newItems.length > 0) {
      const alreadySentCount = sentItems.size;
      const viewed = activeOffers
        .filter((offer) => newItems.includes(offer.offerId))
        .map((offer, index: number) => ({
          item_id: offer.offerId,
          item_name: removeEmptyElements(offer.description),
          affiliation: "Personalized offers",
          index: alreadySentCount + index + 1,
          item_list_name: "Personalized Offers",
          item_list_id: "5",
        }));

      dataLayerProductViewList({
        event: "view_item_list",
        ecommerce: {
          item_list_name: "Personalized Offers",
          item_list_id: "5",
          items: viewed,
        },
      });

      setSentItems((prev) => new Set([...prev, ...newItems]));
    }

    if (visibleItems.size === activeOffers.length) {
      setIsAllSent(true);
    }
  }, [visibleItems, sentItems, activeOffers, isAllSent]);

  const mulesoftOffers = useCallback(async () => {
    try {
      const filtered = activeOffers?.filter(
        (o: any) => o?.bannerInd?.includes(process.env.NEXT_PUBLIC_BANNER)
      );
      setOffers(filtered ?? []);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching Mulesoft offers:", err);
    }
  }, [activeOffers]);
  useEffect(() => {
    mulesoftOffers();
  }, [userLoggedIn, activeOffers]);

  useEffect(() => {
    const arr = [
      ...(favOffers?.basketBuilderOffers ?? []),
      ...(favOffers?.favouriteOffers ?? []),
    ];
    setAddedToListOffers(arr);
  }, [favOffers]);

  return (
    <Box
      as="div"
      className="text-center w-full relative left-0 top-0 z-[9] transition-all duration-700 ease-in-out rounded-lg h-[100%] lg:h-[calc(100%-54px)] offer-inside"
    >
      {!userLoggedIn ? (
        <UserLoggedInFallback
          description="Sign in now to unlock your exclusive offers."
          title="Don't miss out on new deals!"
          actionOneText="Log In"
          actionTwoText="Sign Up"
          actionOne={() => loadLogin("login")}
          actionTwo={() => loadLogin("register")}
        />
      ) : !scene?.cardNumber ? (
        <UserLoggedInFallback
          title="Don't miss out on new deals!"
          description="Add Scene+ to see your exclusive offers."
          actionOneText="Add your Scene+ Card"
          actionOne={() => {
            dispatch(
              sceneStatesTrigger({
                popupType: "addCard",
                suppression: false,
              })
            );
          }}
        />
      ) : (
        <EmblaExclusiveSlider
          addedToListOffers={addedToListOffers}
          activeOffers={offers}
          onVisibilityChange={handleVisibilityChange}
          openDialog={openDialog}
          isOfferLoaded={isOfferLoaded}
          headingParam={headingParam}
        />
      )}

      {isOpen && (
        <Toast
          message={
            <Box
              as="div"
              className="flex flex-col md:justify-between md:flex-row items-start p-0 gap-[10px]"
            >
              <Text as="p" className="text-body text-black !font-light pb-[0px]">
                <Text as="span" className="!font-normal">
                  Your offer was successfully added to your Shopping List.
                </Text>
              </Text>
              <Link
                href="/manage-list"
                className="block ml-[0px] font-bold text-primary600 text-body underline hover:no-underline"
              >
                View List
              </Link>
            </Box>
          }
          onClose={() => { }}
          position="top"
          autoCloseDuration={3000}
          closeButton={
            <span
              onClick={closeDialog}
              className="icon-close text-black font-bold ml-3 cursor-pointer"
            />
          }
          className={`max-w-[90%] md:max-w-[575px] w-[100%] outline-none bg-primary100 top-[150px] !text-left`}
        />
      )}
    </Box>
  );
}
